import { Fragment, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { getEvent, type EventItem } from '../data/events';
import EventShowroom from '../components/EventShowroom';

const ANREDE_OPTIONS = [
  { value: '', label: '– Bitte wählen –' },
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
  { value: 'Divers', label: 'Divers' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof. Dr.', label: 'Prof. Dr.' },
];

function EventDetail() {
  const { eventId } = useParams();
  const event = getEvent(eventId);

  const clientRef = useRef<ReturnType<typeof generateClient<Schema>> | null>(null);
  const getClient = () => {
    if (!clientRef.current) {
      clientRef.current = generateClient<Schema>();
    }
    return clientRef.current;
  };

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    unternehmenInstitution: '',
    anrede: '',
    vorname: '',
    nachname: '',
    position: '',
    email: '',
    telefon: '',
    anmerkung: '',
    projektUpdates: false,
  });
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionSubmitted, setQuestionSubmitted] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [questionData, setQuestionData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    frage: '',
  });
  const [formMode, setFormMode] = useState<'anmeldung' | 'frage'>('anmeldung');
  const [registerConfirmHeight, setRegisterConfirmHeight] = useState<number | null>(null);
  const [questionConfirmHeight, setQuestionConfirmHeight] = useState<number | null>(null);
  const formToggleRef = useRef<HTMLDivElement>(null);
  const registerPanelRef = useRef<HTMLDivElement>(null);
  const questionPanelRef = useRef<HTMLDivElement>(null);
  const registerConfirmRef = useRef<HTMLHeadingElement>(null);
  const questionConfirmRef = useRef<HTMLHeadingElement>(null);
  const formModeRef = useRef(formMode);

  useLayoutEffect(() => {
    const root = formToggleRef.current;
    const indicator = root?.querySelector<HTMLElement>('.event-form-toggle-indicator');
    if (!root || !indicator) return;

    const animate = formModeRef.current !== formMode;
    formModeRef.current = formMode;

    const place = (withMotion: boolean) => {
      const selected = root.querySelector<HTMLButtonElement>('[aria-selected="true"]');
      if (!selected) return;
      const stacked = getComputedStyle(root).flexDirection === 'column';
      const width = stacked ? '100%' : `${selected.offsetWidth}px`;
      const height = stacked ? `${selected.offsetHeight}px` : '100%';
      const transform = `translate(${selected.offsetLeft}px, ${selected.offsetTop}px)`;
      if (
        indicator.style.width === width &&
        indicator.style.height === height &&
        indicator.style.transform === transform
      ) {
        return;
      }
      indicator.style.transition = withMotion ? '' : 'none';
      indicator.style.width = width;
      indicator.style.height = height;
      indicator.style.transform = transform;
      if (!withMotion) {
        indicator.getBoundingClientRect();
        indicator.style.transition = '';
      }
    };

    place(animate);
    const observer = new ResizeObserver(() => place(false));
    observer.observe(root);
    return () => observer.disconnect();
  }, [formMode]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSubmitted(false);
    setError(null);
    setLoading(false);
    setQuestionSubmitted(false);
    setQuestionError(null);
    setQuestionLoading(false);
    setRegisterConfirmHeight(null);
    setQuestionConfirmHeight(null);
    setFormMode(window.location.hash === '#frage' ? 'frage' : 'anmeldung');
  }, [eventId]);

  useEffect(() => {
    if (submitted) registerConfirmRef.current?.focus();
  }, [submitted]);

  useEffect(() => {
    if (questionSubmitted) questionConfirmRef.current?.focus();
  }, [questionSubmitted]);

  if (!event) {
    return <Navigate to="/events" replace />;
  }

  const fieldId = (name: string) => `${event.id}-${name}`;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const anmerkung = formData.anmerkung.trim();
    const message = [
      `Event-Anmeldung: ${event.title}`,
      `Event-ID: ${event.id}`,
      `Termin: ${event.meta}`,
      '',
      'Anmerkung:',
      anmerkung || '–',
    ].join('\n');

    try {
      await getClient().models.ContactSubmission.create(
        {
          unternehmenInstitution: formData.unternehmenInstitution.trim(),
          anrede: formData.anrede || undefined,
          vorname: formData.vorname.trim(),
          nachname: formData.nachname.trim(),
          position: formData.position.trim() || undefined,
          email: formData.email.trim(),
          telefon: formData.telefon.trim() || undefined,
          message,
          interesseAustausch: false,
          projektUpdates: formData.projektUpdates,
        },
        { authMode: 'identityPool' }
      );
      setRegisterConfirmHeight(registerPanelRef.current?.offsetHeight ?? null);
      setSubmitted(true);
      setLoading(false);
    } catch (err) {
      const messageText = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      setError(messageText);
      setLoading(false);
    }
  };

  const handleQuestionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setQuestionData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setQuestionError(null);
    setQuestionLoading(true);

    const message = [
      `Event-Frage: ${event.title}`,
      `Event-ID: ${event.id}`,
      `Termin: ${event.meta}`,
      '',
      'Frage:',
      questionData.frage.trim(),
    ].join('\n');

    try {
      await getClient().models.ContactSubmission.create(
        {
          unternehmenInstitution: '–',
          vorname: questionData.vorname.trim(),
          nachname: questionData.nachname.trim(),
          email: questionData.email.trim(),
          message,
          interesseAustausch: false,
          projektUpdates: false,
        },
        { authMode: 'identityPool' }
      );
      setQuestionConfirmHeight(questionPanelRef.current?.offsetHeight ?? null);
      setQuestionSubmitted(true);
      setQuestionLoading(false);
    } catch (err) {
      const messageText = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      setQuestionError(messageText);
      setQuestionLoading(false);
    }
  };

  const agendaContent = event.agenda && event.agenda.length > 0 && (
    <>
      <h2 id={event.series ? 'event-agenda-heading' : undefined}>Ablauf</h2>
      {event.agendaNote && <p>{event.agendaNote}</p>}
      <ol className="event-agenda">
        {event.agenda.map((item) => (
          <li key={`${item.time}-${item.title}`} className="event-agenda-item">
            <p className="event-agenda-time">{item.time}</p>
            <div>
              <p className="event-agenda-title">{item.title}</p>
              {item.text && <p className="event-agenda-text">{item.text}</p>}
              {item.phases && item.phases.length > 0 && (
                <ol className="event-slot-list" aria-label="Drei thematische Slots">
                  {(item.topics && item.topics.length > 0 ? item.topics : ['']).map((topic) => (
                    <li key={topic || 'slot'} className="event-slot-row">
                      {topic && <p className="event-slot-topic">{topic}</p>}
                      <ol className="event-slot-phases" aria-label={topic || 'Aufbau eines Slots'}>
                        {item.phases!.map((phase) => (
                          <li key={phase.label} style={{ flex: `${phase.minutes} 1 0` }}>
                            <span className="event-slot-phase-label">{phase.label}</span>
                            <span className="event-slot-phase-time">{phase.minutes} min</span>
                          </li>
                        ))}
                      </ol>
                    </li>
                  ))}
                </ol>
              )}
              {item.points && item.points.length > 0 && (
                <ul>
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
    </>
  );

  return (
    <div className="home-container">
      <section className="home-section event-detail-hero" aria-labelledby="event-detail-heading">
        <div className="section-inner">
          {event.date && (
            <p className="event-date">
              <time dateTime={event.dateISO}>{event.date}</time>
            </p>
          )}
          <h1 className="event-title" id="event-detail-heading">{event.title}</h1>
          <div className="event-layout">
            <div className="event-media">
              <img src={event.image} alt={event.alt} />
            </div>
            <div className="event-copy">
              <p className="event-meta">
                {event.meta.split(' · ')[0]}
                {event.meta.includes(' · ') && (
                  <>
                    <br />
                    {event.meta.split(' · ')[1]}
                  </>
                )}
              </p>
              <p className="event-summary">
                {event.summary.split(/(?<=\.)\s+/).map((sentence, index) => (
                  <Fragment key={index}>
                    {index > 0 && <br />}
                    {sentence}
                  </Fragment>
                ))}
              </p>
            </div>
          </div>
        </div>
      </section>

      {event.series && (
        <section className="home-section event-detail-series" aria-labelledby="event-series-heading">
          <div className="section-inner">
            <h2 id="event-series-heading">{event.series.title}</h2>
            {event.series.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <ul className="event-pillar-grid">
              {event.series.pillars.map((pillar) => (
                <li key={pillar.name} className="event-pillar-tile">
                  <h3>{pillar.name}</h3>
                  <p>{pillar.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {event.series && agendaContent && (
        <section className="home-section event-detail-agenda" aria-labelledby="event-agenda-heading">
          <div className="section-inner">
            {agendaContent}
          </div>
        </section>
      )}

      {event.showroom && <EventShowroom showroom={event.showroom} />}

      <section className="home-section event-detail-body" aria-label="Informationen">
        <div className="section-inner">
          {event.series && event.description.length > 0 && <h2>Dieser Abend</h2>}
          {event.description.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}

          {!event.series && agendaContent}

          {!(event.speakers && event.speakers.length > 0) && <EventFacts event={event} />}
        </div>
      </section>

      {event.speakers && event.speakers.length > 0 && (
        <section className="home-section event-detail-speakers" aria-labelledby="event-speakers-heading">
          <div className="section-inner">
            <h2 id="event-speakers-heading">Referenten</h2>
            <ul className="event-speaker-grid">
              {event.speakers.map((speaker) => (
                <li key={speaker.name} className="event-speaker">
                  <img src={speaker.image} alt={speaker.alt} />
                  <div>
                    <h3>{speaker.name}</h3>
                    {speaker.text.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {event.speakers && event.speakers.length > 0 && (event.location || event.organizer || event.contact) && (
        <section className="home-section event-detail-body" aria-label="Ort und Veranstalter">
          <div className="section-inner">
            <EventFacts event={event} />
          </div>
        </section>
      )}

      <section className="home-section event-detail-forms" id="anmeldung" aria-labelledby="event-form-heading">
        <div id="frage" />
        <div className="section-inner">
          <h2 id="event-form-heading" className="event-form-heading">
            {formMode === 'anmeldung' ? 'Anmeldung' : 'Kontakt zum Veranstalter'}
          </h2>
          <div
            className="event-form-toggle"
            role="tablist"
            aria-label="Formular"
            ref={formToggleRef}
          >
            <span className="event-form-toggle-indicator" aria-hidden="true" />
            <button
              type="button"
              role="tab"
              id="event-tab-anmeldung"
              aria-selected={formMode === 'anmeldung'}
              aria-controls="event-panel-anmeldung"
              onClick={() => setFormMode('anmeldung')}
            >
              Anmeldung
            </button>
            <button
              type="button"
              role="tab"
              id="event-tab-frage"
              aria-selected={formMode === 'frage'}
              aria-controls="event-panel-frage"
              onClick={() => setFormMode('frage')}
            >
              Kontakt zum Veranstalter
            </button>
          </div>

          {formMode === 'anmeldung' ? (
            <div
              role="tabpanel"
              id="event-panel-anmeldung"
              aria-labelledby="event-tab-anmeldung"
              ref={registerPanelRef}
            >
              {submitted ? (
                <EventFormConfirmation
                  title="Ihre Anmeldung ist abgeschlossen"
                  lead={`Vielen Dank${formData.vorname.trim() ? `, ${formData.vorname.trim()}` : ''}. Ihre Anmeldung für „${event.title}“ ist abgeschlossen.`}
                  followUp={`Sie müssen nichts weiter unternehmen. Zwei Tage vor der Veranstaltung erhalten Sie eine Erinnerung per E-Mail an ${formData.email.trim()}.`}
                  details={[
                    {
                      label: 'Name',
                      value: [formData.vorname, formData.nachname].map((part) => part.trim()).filter(Boolean).join(' '),
                    },
                    { label: 'E-Mail', value: formData.email.trim() },
                    { label: 'Unternehmen', value: formData.unternehmenInstitution.trim() },
                  ]}
                  event={event}
                  minHeight={registerConfirmHeight}
                  headingRef={registerConfirmRef}
                />
              ) : (
                <>
                  <p className="event-register-intro">
                    Melden Sie sich für „{event.title}“ an. Die mit * markierten Felder sind Pflichtfelder.
                  </p>

              {error && (
                <div className="kontakt-error" role="alert">
                  {error}
                </div>
              )}

              <form className="kontakt-form event-detail-form" onSubmit={handleSubmit}>
                <div className="form-row form-row-split">
                  <div className="form-row-half">
                    <label htmlFor={fieldId('vorname')}>
                      Vorname <span className="required">*</span>
                    </label>
                    <input
                      id={fieldId('vorname')}
                      name="vorname"
                      type="text"
                      autoComplete="given-name"
                      required
                      value={formData.vorname}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-row-half">
                    <label htmlFor={fieldId('nachname')}>
                      Nachname <span className="required">*</span>
                    </label>
                    <input
                      id={fieldId('nachname')}
                      name="nachname"
                      type="text"
                      autoComplete="family-name"
                      required
                      value={formData.nachname}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row form-row-split">
                  <div className="form-row-half">
                    <label htmlFor={fieldId('anrede')}>Anrede</label>
                    <select
                      id={fieldId('anrede')}
                      name="anrede"
                      autoComplete="honorific-prefix"
                      value={formData.anrede}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      {ANREDE_OPTIONS.map((opt) => (
                        <option key={opt.value || 'empty'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row-half">
                    <label htmlFor={fieldId('email')}>
                      E-Mail <span className="required">*</span>
                    </label>
                    <input
                      id={fieldId('email')}
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row form-row-split">
                  <div className="form-row-half">
                    <label htmlFor={fieldId('unternehmenInstitution')}>
                      Unternehmen/Institution <span className="required">*</span>
                    </label>
                    <input
                      id={fieldId('unternehmenInstitution')}
                      name="unternehmenInstitution"
                      type="text"
                      autoComplete="organization"
                      required
                      value={formData.unternehmenInstitution}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-row-half">
                    <label htmlFor={fieldId('position')}>Position</label>
                    <input
                      id={fieldId('position')}
                      name="position"
                      type="text"
                      autoComplete="organization-title"
                      value={formData.position}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <label htmlFor={fieldId('telefon')}>Telefon</label>
                  <input
                    id={fieldId('telefon')}
                    name="telefon"
                    type="tel"
                    autoComplete="tel"
                    value={formData.telefon}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor={fieldId('anmerkung')}>Anmerkung</label>
                  <textarea
                    id={fieldId('anmerkung')}
                    name="anmerkung"
                    rows={4}
                    value={formData.anmerkung}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                <div className="form-row form-row-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="projektUpdates"
                      checked={formData.projektUpdates}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    Ich möchte über das Projekt und weitere Veranstaltungen informiert werden.
                  </label>
                </div>

                <div className="form-row form-row-submit">
                  <button type="submit" className="kontakt-submit" disabled={loading}>
                    {loading ? 'Wird gesendet…' : 'Anmeldung absenden'}
                  </button>
                  <p className="event-register-privacy">
                    Mit dem Absenden willigen Sie in die Verarbeitung Ihrer Angaben zur
                    Veranstaltungsanmeldung ein. Details finden Sie in der{' '}
                    <Link to="/datenschutz">Datenschutzerklärung</Link>.
                  </p>
                </div>
              </form>
                </>
              )}
            </div>
          ) : (
            <div
              role="tabpanel"
              id="event-panel-frage"
              aria-labelledby="event-tab-frage"
              ref={questionPanelRef}
            >
              {questionSubmitted ? (
                <EventFormConfirmation
                  title="Ihre Nachricht ist eingegangen"
                  lead={`Vielen Dank${questionData.vorname.trim() ? `, ${questionData.vorname.trim()}` : ''}. Wir haben Ihre Nachricht zu „${event.title}“ erhalten.`}
                  followUp={`Wir antworten in Kürze an ${questionData.email.trim()}.`}
                  details={[
                    {
                      label: 'Name',
                      value: [questionData.vorname, questionData.nachname].map((part) => part.trim()).filter(Boolean).join(' '),
                    },
                    { label: 'E-Mail', value: questionData.email.trim() },
                  ]}
                  event={event}
                  minHeight={questionConfirmHeight}
                  headingRef={questionConfirmRef}
                />
              ) : (
                <>
                  <p className="event-register-intro">
                    {event.questionIntro ?? (
                      <>Sie haben eine Frage zu „{event.title}“? Schreiben Sie dem Veranstalter.</>
                    )}
                  </p>

              {questionError && (
                <div className="kontakt-error" role="alert">
                  {questionError}
                </div>
              )}

              <form className="kontakt-form event-detail-form" onSubmit={handleQuestionSubmit}>
                <div className="form-row form-row-split">
                  <div className="form-row-half">
                    <label htmlFor={fieldId('frage-vorname')}>
                      Vorname <span className="required">*</span>
                    </label>
                    <input
                      id={fieldId('frage-vorname')}
                      name="vorname"
                      type="text"
                      autoComplete="given-name"
                      required
                      value={questionData.vorname}
                      onChange={handleQuestionChange}
                      disabled={questionLoading}
                    />
                  </div>
                  <div className="form-row-half">
                    <label htmlFor={fieldId('frage-nachname')}>
                      Nachname <span className="required">*</span>
                    </label>
                    <input
                      id={fieldId('frage-nachname')}
                      name="nachname"
                      type="text"
                      autoComplete="family-name"
                      required
                      value={questionData.nachname}
                      onChange={handleQuestionChange}
                      disabled={questionLoading}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <label htmlFor={fieldId('frage-email')}>
                    E-Mail <span className="required">*</span>
                  </label>
                  <input
                    id={fieldId('frage-email')}
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={questionData.email}
                    onChange={handleQuestionChange}
                    disabled={questionLoading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor={fieldId('frage-text')}>
                    Nachricht <span className="required">*</span>
                  </label>
                  <textarea
                    id={fieldId('frage-text')}
                    name="frage"
                    rows={4}
                    required
                    value={questionData.frage}
                    onChange={handleQuestionChange}
                    disabled={questionLoading}
                  />
                </div>

                <div className="form-row form-row-submit">
                  <button type="submit" className="kontakt-submit" disabled={questionLoading}>
                    {questionLoading ? 'Wird gesendet…' : 'Nachricht senden'}
                  </button>
                  <p className="event-register-privacy">
                    Mit dem Absenden willigen Sie in die Verarbeitung Ihrer Angaben zur
                    Beantwortung Ihrer Frage ein. Details finden Sie in der{' '}
                    <Link to="/datenschutz">Datenschutzerklärung</Link>.
                  </p>
                </div>
              </form>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EventFormConfirmation({
  title,
  lead,
  followUp,
  details,
  event,
  minHeight,
  headingRef,
}: {
  title: string;
  lead: string;
  followUp: string;
  details: { label: string; value: string }[];
  event: EventItem;
  minHeight: number | null;
  headingRef: RefObject<HTMLHeadingElement>;
}) {
  const [when, place] = event.meta.split(' · ');
  const facts = [
    ...details.filter((item) => item.value),
    when ? { label: 'Termin', value: when } : null,
    place ? { label: 'Ort', value: place } : null,
    event.organizer
      ? {
          label: 'Veranstalter',
          value: event.organizerNote ? `${event.organizer}, ${event.organizerNote}` : event.organizer,
        }
      : null,
    event.contact ? { label: 'Ansprechperson', value: event.contact } : null,
  ].filter((item): item is { label: string; value: string } => item !== null);

  return (
    <div
      className="event-form-confirmation"
      role="status"
      style={minHeight ? { minHeight } : undefined}
    >
      <div>
        <h3 ref={headingRef} tabIndex={-1}>{title}</h3>
        <p>{lead}</p>
      </div>
      <p className="event-form-confirmation-note">{followUp}</p>
      <ul className="event-form-confirmation-facts">
        {facts.map((item) => (
          <li key={item.label}>
            <span>{item.label}</span>
            <span>{item.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EventFacts({ event }: { event: EventItem }) {
  if (!event.location && !event.organizer && !event.contact) return null;

  return (
    <div className="event-detail-facts">
      {event.location && (
        <div>
          <h2>Ort</h2>
          <p className="event-location-name">{event.location.name}</p>
          <p>
            {event.location.lines.map((line) => (
              <span key={line}>
                {line}
                <br />
              </span>
            ))}
            {event.location.phone && (
              <>
                <a href={`tel:${event.location.phone.replace(/\s/g, '')}`}>{event.location.phone}</a>
                <br />
              </>
            )}
            {event.location.email && (
              <a href={`mailto:${event.location.email}`}>{event.location.email}</a>
            )}
          </p>
        </div>
      )}

      {(event.organizer || event.contact) && (
        <div>
          {event.organizer && (
            <>
              <h2>Veranstalter</h2>
              <p className="event-location-name">{event.organizer}</p>
              {event.organizerNote && <p className="event-organizer-note">{event.organizerNote}</p>}
            </>
          )}
          {event.contact && (
            <p className="event-organizer">
              <strong>Ansprechperson:</strong> {event.contact}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default EventDetail;
